import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useCustomer } from "../context/CustomerContext";
import { formatApiErrorDetail } from "../lib/api";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogle = () => {
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function CustomerLogin() {
  const navigate = useNavigate();
  const { customer, loading, login, register } = useCustomer();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && customer) navigate("/account");
  }, [customer, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email.trim().toLowerCase(), password);
      } else {
        await register(email.trim().toLowerCase(), password, name.trim());
      }
      toast.success("Welcome to Drift");
      navigate("/account");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#04070A] min-h-screen flex flex-col">
      <Nav />
      <div className="flex-1 flex items-center justify-center px-6 pt-32 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
          className="w-full max-w-md border border-white/10 bg-[#0A1118] p-8"
          data-testid="customer-auth-card"
        >
          <span className="font-mono-plex text-[10px] tracking-[0.25em] uppercase text-orange-500">Angler Account</span>
          <h1 className="font-display text-4xl uppercase tracking-tight mt-3 leading-none">
            {mode === "login" ? "Sign In" : "Create Account"}
          </h1>
          <p className="text-slate-400 text-sm mt-3">
            Save your custom recipes, reorder favorites, and track your orders. Buying as a guest still works — no account required.
          </p>

          <button
            onClick={startGoogle}
            className="w-full mt-7 border border-white/20 py-3.5 font-mono-plex text-xs tracking-[0.2em] uppercase text-slate-200 hover:bg-white/5 transition-colors flex items-center justify-center gap-3"
            data-testid="google-signin-button"
          >
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" />
            Continue with Google
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px bg-white/10 flex-1" />
            <span className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-600">or</span>
            <div className="h-px bg-white/10 flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-transparent border border-white/15 mt-2 p-3 focus:border-orange-500 focus:outline-none text-sm text-slate-200"
                  data-testid="auth-name"
                />
              </div>
            )}
            <div>
              <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border border-white/15 mt-2 p-3 focus:border-orange-500 focus:outline-none text-sm text-slate-200"
                data-testid="auth-email"
              />
            </div>
            <div>
              <label className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border border-white/15 mt-2 p-3 focus:border-orange-500 focus:outline-none text-sm text-slate-200"
                data-testid="auth-password"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-[0.2em] uppercase py-4 hover:bg-orange-600 active:scale-[0.98] transition-all disabled:opacity-50"
              data-testid="auth-submit"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <button
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="mt-6 font-mono-plex text-xs tracking-wide text-slate-400 hover:text-orange-400 transition-colors"
            data-testid="auth-toggle-mode"
          >
            {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
          </button>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
